<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import metaLeadgenConfigsAPI from 'dashboard/api/metaLeadgenConfigs';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import LeadAssignmentPool from 'dashboard/components-next/leads/LeadAssignmentPool.vue';
import { useWhatsappTemplates } from 'dashboard/composables/useWhatsappTemplates';
import {
  ORIGEM_CAIXA,
  ORIGEM_SOCIAL,
  opcoesDePagina,
  valorDaPagina,
  camposDaPagina,
} from './helper/metaLeadgenPages';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const WHATSAPP_PROVIDERS = [
  'evolution',
  'waha',
  'uazapi',
  'baileys',
  'notificame',
  'whatsapp_cloud',
];

const DEFAULT_FIELD_MAPPING = [
  { source: 'full_name', target: 'name' },
  { source: 'email', target: 'email' },
  { source: 'phone_number', target: 'phone' },
];

const TARGET_FIELDS = ['name', 'email', 'phone'];

const isLoading = ref(true);
const isSaving = ref(false);
const configs = ref([]);

const blankForm = () => ({
  id: null,
  channel_facebook_page_id: '',
  social_account_id: '',
  zernio_form_ids: [],
  funnel_id: '',
  stage_name: '',
  whatsapp_inbox_id: '',
  first_message_template: '',
  first_message_template_name: '',
  first_message_ai: false,
  enabled: true,
  field_mapping: DEFAULT_FIELD_MAPPING.map(row => ({ ...row })),
  assignment_user_ids: [],
  assignment_routing: {},
  assignment_online_only: false,
});

const form = ref(blankForm());
const showForm = ref(false);

const inboxes = computed(() => getters['inboxes/getInboxes'].value);

const facebookInboxes = computed(() =>
  inboxes.value.filter(inbox => inbox.channel_type === INBOX_TYPES.FB)
);

const whatsappInboxes = computed(() =>
  inboxes.value.filter(inbox => {
    if (inbox.channel_type === INBOX_TYPES.WHATSAPP) return true;
    if (inbox.channel_type === INBOX_TYPES.API) {
      const attrs = inbox.additional_attributes || {};
      if (attrs.channel_identifier === 'whatsapp') return true;
      const provider = (inbox.provider || attrs.provider || '').toLowerCase();
      if (WHATSAPP_PROVIDERS.includes(provider)) return true;
      if (inbox.name?.toLowerCase().includes('whatsapp')) return true;
    }
    return false;
  })
);

const funnels = computed(() => getters['funnels/getFunnels'].value);
// Rodízio: só usuários da conta; quem não for membro da caixa de WhatsApp
// escolhida não recebe lead (o backend ignora), por isso a dica na tela.
const accountAgents = computed(() => getters['agents/getAgents'].value || []);

// Caixa geral da integração: quem usa um número só configura aqui. Se esse
// número for oficial, texto livre não sai — a Meta exige template aprovado.
const {
  ehOficial: caixaEhOficial,
  carregar: carregarTemplates,
  templatesDe,
  estaCarregando,
} = useWhatsappTemplates(whatsappInboxes);

const caixaGeralOficial = computed(() =>
  caixaEhOficial(form.value.whatsapp_inbox_id)
);

watch(
  () => form.value.whatsapp_inbox_id,
  (nova, anterior) => {
    // Trocar de caixa invalida o template: ele pertence à caixa anterior.
    if (anterior !== undefined && nova !== anterior) {
      form.value.first_message_template_name = '';
    }
    carregarTemplates(nova);
  },
  { immediate: true }
);
const formStages = computed(() => {
  if (!form.value.funnel_id) return [];
  return getters['funnels/getFunnelStages'].value(form.value.funnel_id);
});

const facebookPageLabel = channelId => {
  const inbox = facebookInboxes.value.find(
    item => item.channel_id === channelId
  );
  return inbox ? inbox.name : `#${channelId}`;
};

const funnelLabel = funnelId => {
  const funnel = funnels.value.find(item => item.id === funnelId);
  return funnel ? funnel.name : `#${funnelId}`;
};

// Páginas conectadas pelo Estúdio Social (Zernio). Existem porque o App
// Review negou `pages_show_list` ao nosso app: nenhum cliente consegue mais
// conectar a página como CAIXA, que era a única fonte que esta tela lia.
const socialPages = ref([]);

const loadSources = async () => {
  try {
    const { data } = await metaLeadgenConfigsAPI.sources();
    socialPages.value = data.social_pages || [];
  } catch {
    socialPages.value = [];
  }
};

// Formulários da página social escolhida, para a conta marcar só os que quer.
const socialForms = computed(() => {
  const pagina = socialPages.value.find(
    p => String(p.id) === String(form.value.social_account_id)
  );
  return pagina ? pagina.forms || [] : [];
});

// Uma página de imobiliária tem quase 20 formulários com nomes parecidos
// ("Formulário Rokni - Kleber (V1)"), e o <select multiple> mostrava três
// linhas cortadas que só marcavam com ctrl. Vira lista com busca.
const buscaFormulario = ref('');

const formulariosFiltrados = computed(() => {
  const termo = buscaFormulario.value.trim().toLowerCase();
  if (!termo) return socialForms.value;
  return socialForms.value.filter(formulario =>
    (formulario.name || '').toLowerCase().includes(termo)
  );
});

const formularioMarcado = id => form.value.zernio_form_ids.includes(id);

const alternarFormulario = id => {
  const marcados = form.value.zernio_form_ids;
  form.value.zernio_form_ids = marcados.includes(id)
    ? marcados.filter(item => item !== id)
    : [...marcados, id];
};

const todosMarcados = computed(
  () =>
    socialForms.value.length > 0 &&
    socialForms.value.every(formulario => formularioMarcado(formulario.id))
);

const alternarTodosFormularios = () => {
  form.value.zernio_form_ids = todosMarcados.value
    ? []
    : socialForms.value.map(formulario => formulario.id);
};

// Uma lista só: a caixa do Facebook (que praticamente ninguém tem) e a página
// conectada pelo Estúdio Social aparecem juntas, com a origem como grupo.
const opcoesPagina = computed(() =>
  opcoesDePagina({
    facebookInboxes: facebookInboxes.value,
    socialPages: socialPages.value,
  })
);

const opcoesCaixa = computed(() =>
  opcoesPagina.value.filter(opcao => opcao.origem === ORIGEM_CAIXA)
);

const opcoesSocial = computed(() =>
  opcoesPagina.value.filter(opcao => opcao.origem === ORIGEM_SOCIAL)
);

const paginaSelecionada = computed({
  get: () => valorDaPagina(form.value),
  set: valor => {
    const campos = camposDaPagina(valor);
    // Trocar de página invalida os formulários marcados: eles são de outra.
    if (campos.social_account_id !== form.value.social_account_id) {
      form.value.zernio_form_ids = [];
    }
    form.value.channel_facebook_page_id = campos.channel_facebook_page_id;
    form.value.social_account_id = campos.social_account_id;
  },
});

const ehPaginaSocial = computed(
  () => valorDaPagina(form.value).split(':')[0] === ORIGEM_SOCIAL
);

// Com uma única página conectada, escolher por ela: o cliente abria a tela,
// via o seletor vazio e achava que a integração não tinha chegado.
watch([showForm, opcoesPagina], () => {
  if (!showForm.value || paginaSelecionada.value) return;
  if (opcoesPagina.value.length !== 1) return;
  paginaSelecionada.value = opcoesPagina.value[0].value;
});

const hasAnySource = computed(() => opcoesPagina.value.length > 0);

// Config vinda do Estúdio Social não tem caixa: sem isto a lista mostrava
// "#undefined" no lugar do nome da página.
const paginaLabel = config => {
  if (config.social_account_id) {
    const pagina = socialPages.value.find(
      item => String(item.id) === String(config.social_account_id)
    );
    return pagina ? pagina.name : `#${config.social_account_id}`;
  }
  return facebookPageLabel(config.channel_facebook_page_id);
};

const loadConfigs = async () => {
  try {
    const { data } = await metaLeadgenConfigsAPI.get();
    configs.value = data.payload || [];
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.LOAD_ERROR'));
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('funnels/get'),
      store.dispatch('agents/get'),
      loadConfigs(),
      loadSources(),
    ]);
  } finally {
    isLoading.value = false;
  }
});

// Sem funil escolhido não existe etapa: a captura entrega contato e conversa, e
// quem decide o funil depois é a IA — o caminho de quem tem vários funis.
const onSelectFunnel = () => {
  if (!form.value.funnel_id) {
    form.value.stage_name = '';
    return;
  }
  const validStage = formStages.value.some(
    stage => stage.name === form.value.stage_name
  );
  if (!validStage) {
    form.value.stage_name = formStages.value[0]?.name || '';
  }
};

const openCreate = () => {
  form.value = blankForm();
  showForm.value = true;
};

const openEdit = config => {
  const mappingEntries = Object.entries(config.field_mapping || {}).map(
    ([source, target]) => ({ source, target })
  );
  form.value = {
    id: config.id,
    channel_facebook_page_id: config.channel_facebook_page_id || '',
    social_account_id: config.social_account_id || '',
    zernio_form_ids: config.zernio_form_ids || [],
    funnel_id: config.funnel_id || '',
    stage_name: config.stage_name || '',
    whatsapp_inbox_id: config.whatsapp_inbox_id || '',
    first_message_template: config.first_message_template || '',
    first_message_template_name: config.first_message_template_name || '',
    first_message_ai: Boolean(config.first_message_ai),
    enabled: config.enabled,
    field_mapping: mappingEntries.length
      ? mappingEntries
      : DEFAULT_FIELD_MAPPING.map(row => ({ ...row })),
    assignment_user_ids: [...(config.assignment_user_ids || [])],
    assignment_routing: { ...(config.assignment_routing || {}) },
    assignment_online_only: Boolean(config.assignment_online_only),
  };
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
  form.value = blankForm();
};

const addMappingRow = () => {
  form.value.field_mapping.push({ source: '', target: 'name' });
};

const removeMappingRow = index => {
  form.value.field_mapping.splice(index, 1);
};

const buildPayload = () => {
  const fieldMapping = {};
  form.value.field_mapping.forEach(row => {
    if (row.source && row.target) {
      fieldMapping[row.source.trim()] = row.target;
    }
  });
  return {
    channel_facebook_page_id: form.value.channel_facebook_page_id || null,
    social_account_id: form.value.social_account_id || null,
    zernio_form_ids: form.value.zernio_form_ids,
    funnel_id: form.value.funnel_id || null,
    stage_name: form.value.funnel_id ? form.value.stage_name : null,
    whatsapp_inbox_id: form.value.whatsapp_inbox_id || null,
    first_message_template: form.value.first_message_template,
    first_message_template_name: form.value.first_message_template_name,
    first_message_ai: form.value.first_message_ai,
    enabled: form.value.enabled,
    field_mapping: fieldMapping,
    assignment_user_ids: form.value.assignment_user_ids,
    assignment_routing: form.value.assignment_routing,
    assignment_online_only: form.value.assignment_online_only,
  };
};

const isFormValid = computed(
  () =>
    Boolean(
      form.value.channel_facebook_page_id || form.value.social_account_id
    ) &&
    (!form.value.funnel_id || Boolean(form.value.stage_name))
);

const saveConfig = async () => {
  if (!isFormValid.value) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.VALIDATION_ERROR'));
    return;
  }
  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (form.value.id) {
      await metaLeadgenConfigsAPI.update(form.value.id, payload);
      useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.UPDATE_SUCCESS'));
    } else {
      await metaLeadgenConfigsAPI.create(payload);
      useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.CREATE_SUCCESS'));
    }
    await loadConfigs();
    cancelForm();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deleteConfig = async config => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTEGRATION_SETTINGS.META_LEADGEN.DELETE_CONFIRM'))) {
    return;
  }
  try {
    await metaLeadgenConfigsAPI.delete(config.id);
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.DELETE_SUCCESS'));
    await loadConfigs();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.DELETE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.META_LEADGEN.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.META_LEADGEN.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.META_LEADGEN.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Intro / requirements -->
        <div
          class="bg-n-alpha-2 border border-n-weak rounded-md p-4 text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.INTRO') }}
        </div>

        <!-- Existing configs list -->
        <div v-if="!showForm" class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.TITLE') }}
            </h3>
            <Button
              icon="i-lucide-plus"
              data-testid="criar-captura"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.ADD')"
              @click="openCreate"
            />
          </div>

          <div
            v-if="!configs.length"
            class="text-sm text-n-slate-11 border border-dashed border-n-weak rounded-md p-6 text-center"
          >
            {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.EMPTY') }}
          </div>

          <div
            v-for="config in configs"
            :key="config.id"
            class="flex items-center justify-between border border-n-weak rounded-md p-4 bg-n-alpha-3"
          >
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2">
                <span class="font-medium text-n-slate-12">
                  {{ paginaLabel(config) }}
                </span>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="
                    config.enabled
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    config.enabled
                      ? $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.ENABLED')
                      : $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.DISABLED')
                  }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                <template v-if="config.funnel_id">
                  {{ funnelLabel(config.funnel_id) }}
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
                  <span aria-hidden="true">&middot;</span>
                  {{ config.stage_name }}
                </template>
                <template v-else>
                  {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FUNNEL_NONE') }}
                </template>
              </p>
            </div>
            <div class="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                icon="i-lucide-pencil"
                :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.EDIT')"
                @click="openEdit(config)"
              />
              <Button
                variant="outline"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.DELETE')"
                @click="deleteConfig(config)"
              />
            </div>
          </div>
        </div>

        <!-- Form -->
        <form
          v-else
          class="flex flex-col gap-4 border border-n-weak rounded-md p-6 bg-n-alpha-3"
          @submit.prevent="saveConfig"
        >
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              form.id
                ? $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.EDIT_TITLE')
                : $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.CREATE_TITLE')
            }}
          </h3>

          <!-- Página de anúncios: caixa do Facebook ou página do Estúdio Social -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.PAGE') }}
            </span>
            <select
              v-model="paginaSelecionada"
              data-testid="seletor-pagina"
              class="form-select"
              :disabled="!hasAnySource"
            >
              <option value="" disabled>
                {{
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.PAGE_PLACEHOLDER')
                }}
              </option>
              <optgroup
                v-if="opcoesSocial.length"
                :label="
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.PAGE_GROUP_SOCIAL')
                "
              >
                <option
                  v-for="opcao in opcoesSocial"
                  :key="opcao.value"
                  :value="opcao.value"
                >
                  {{ opcao.label }}
                </option>
              </optgroup>
              <optgroup
                v-if="opcoesCaixa.length"
                :label="
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.PAGE_GROUP_INBOX')
                "
              >
                <option
                  v-for="opcao in opcoesCaixa"
                  :key="opcao.value"
                  :value="opcao.value"
                >
                  {{ opcao.label }}
                </option>
              </optgroup>
            </select>
            <span
              v-if="!hasAnySource"
              class="text-xs text-yellow-700 dark:text-yellow-500"
            >
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.NO_PAGES') }}
            </span>
            <span v-else-if="ehPaginaSocial" class="text-xs text-n-slate-11">
              {{
                $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.SOCIAL_PAGE_HINT')
              }}
            </span>
          </label>

          <!-- Formulários da página social -->
          <div v-if="socialForms.length" class="flex flex-col gap-1">
            <div class="flex items-baseline justify-between gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  form.zernio_form_ids.length
                    ? $t(
                        'INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_SELECTED',
                        {
                          marcados: form.zernio_form_ids.length,
                          total: socialForms.length,
                        }
                      )
                    : $t(
                        'INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_ALL'
                      )
                }}
              </span>
            </div>

            <div
              class="flex flex-col rounded-lg border border-n-weak bg-n-solid-2"
            >
              <div class="flex items-center gap-2 border-b border-n-weak p-2">
                <input
                  v-model="buscaFormulario"
                  type="search"
                  data-testid="busca-formulario"
                  class="flex-1 h-8 px-2 text-xs bg-n-alpha-1 border border-n-weak rounded-md text-n-slate-12 placeholder:text-n-slate-10"
                  :placeholder="
                    $t(
                      'INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_SEARCH'
                    )
                  "
                />
                <button
                  type="button"
                  data-testid="alternar-todos-formularios"
                  class="text-xs font-medium text-n-brand whitespace-nowrap px-2 py-1 rounded-md hover:bg-n-alpha-1"
                  @click="alternarTodosFormularios"
                >
                  {{
                    todosMarcados
                      ? $t(
                          'INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_NONE_ACTION'
                        )
                      : $t(
                          'INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_ALL_ACTION'
                        )
                  }}
                </button>
              </div>

              <div
                data-testid="seletor-formularios"
                class="max-h-56 overflow-y-auto p-1"
              >
                <button
                  v-for="formulario in formulariosFiltrados"
                  :key="formulario.id"
                  type="button"
                  data-testid="formulario-item"
                  :aria-pressed="formularioMarcado(formulario.id)"
                  class="flex items-center w-full gap-2 px-2 py-1.5 text-left rounded-md hover:bg-n-alpha-1"
                  @click="alternarFormulario(formulario.id)"
                >
                  <span
                    class="size-3.5 shrink-0"
                    :class="
                      formularioMarcado(formulario.id)
                        ? 'i-lucide-check-square text-n-brand'
                        : 'i-lucide-square text-n-slate-10'
                    "
                  />
                  <span
                    class="flex-1 text-xs truncate"
                    :class="
                      formularioMarcado(formulario.id)
                        ? 'text-n-slate-12 font-medium'
                        : 'text-n-slate-11'
                    "
                  >
                    {{ formulario.name }}
                  </span>
                  <span class="text-[11px] text-n-slate-10 whitespace-nowrap">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_COUNT',
                        { count: formulario.leads_count || 0 }
                      )
                    }}
                  </span>
                </button>
                <p
                  v-if="!formulariosFiltrados.length"
                  class="px-2 py-3 text-xs text-n-slate-11"
                >
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_EMPTY'
                    )
                  }}
                </p>
              </div>
            </div>

            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.LEAD_FORMS_HINT') }}
            </span>
          </div>

          <!-- Funnel -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FUNNEL') }}
            </span>
            <select
              v-model="form.funnel_id"
              data-testid="seletor-funil"
              class="form-select"
              @change="onSelectFunnel"
            >
              <option value="">
                {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FUNNEL_NONE') }}
              </option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.name }}
              </option>
            </select>
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FUNNEL_HINT') }}
            </span>
          </label>

          <!-- Stage -->
          <label v-if="form.funnel_id" class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.STAGE') }}
            </span>
            <select
              v-model="form.stage_name"
              class="form-select"
              :disabled="!form.funnel_id"
            >
              <option value="" disabled>
                {{
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.STAGE_PLACEHOLDER')
                }}
              </option>
              <option
                v-for="stage in formStages"
                :key="stage.name"
                :value="stage.name"
              >
                {{ stage.name }}
              </option>
            </select>
          </label>

          <!-- WhatsApp inbox -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.WHATSAPP_INBOX') }}
            </span>
            <select v-model="form.whatsapp_inbox_id" class="form-select">
              <option value="">
                {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.WHATSAPP_NONE') }}
              </option>
              <option
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.WHATSAPP_HINT') }}
            </span>
          </label>

          <!-- First message template -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE') }}
            </span>
            <textarea
              v-model="form.first_message_template"
              rows="3"
              class="form-textarea"
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE_PLACEHOLDER'
                )
              "
            />
            <span class="text-xs text-n-slate-11">
              {{
                $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE_HINT')
              }}
            </span>
          </label>

          <!-- IA escreve a 1ª mensagem com o texto acima como roteiro. Em
               número oficial não vale: o que sai é o template aprovado. -->
          <label v-if="!caixaGeralOficial" class="flex items-start gap-2">
            <input
              v-model="form.first_message_ai"
              type="checkbox"
              class="mt-1"
            />
            <span class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE_AI')
                }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE_AI_HINT'
                  )
                }}
              </span>
            </span>
          </label>

          <!-- Template da caixa geral: só aparece quando o número é oficial,
               porque é o único caso em que texto livre não é entregue. -->
          <label v-if="caixaGeralOficial" class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('LEAD_ASSIGNMENT.GENERAL_TEMPLATE') }}
            </span>
            <select
              v-model="form.first_message_template_name"
              class="form-select"
            >
              <option value="">
                {{
                  estaCarregando(form.whatsapp_inbox_id)
                    ? $t('LEAD_ASSIGNMENT.TEMPLATE_LOADING')
                    : $t('LEAD_ASSIGNMENT.TEMPLATE_EMPTY')
                }}
              </option>
              <option
                v-for="modelo in templatesDe(form.whatsapp_inbox_id)"
                :key="modelo.id || modelo.name"
                :value="modelo.name"
              >
                {{ modelo.name }}
              </option>
            </select>
            <span class="text-xs text-n-slate-11">
              {{ $t('LEAD_ASSIGNMENT.GENERAL_TEMPLATE_HINT') }}
            </span>
          </label>

          <!-- Field mapping -->
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING_HINT') }}
            </span>
            <div
              v-for="(row, index) in form.field_mapping"
              :key="index"
              class="flex items-center gap-2"
            >
              <input
                v-model="row.source"
                type="text"
                class="form-input flex-1"
                :placeholder="
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING_SOURCE')
                "
              />
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
              <span class="text-n-slate-11" aria-hidden="true">&rarr;</span>
              <select v-model="row.target" class="form-select flex-1">
                <option
                  v-for="field in TARGET_FIELDS"
                  :key="field"
                  :value="field"
                >
                  {{ field }}
                </option>
              </select>
              <Button
                type="button"
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                @click="removeMappingRow(index)"
              />
            </div>
            <Button
              type="button"
              variant="faded"
              size="xs"
              icon="i-lucide-plus"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING_ADD')"
              @click="addMappingRow"
            />
          </div>

          <!-- Rodízio de atendentes: quem atende, por qual número e,
               em número oficial, com qual template. -->
          <LeadAssignmentPool
            v-model:user-ids="form.assignment_user_ids"
            v-model:routing="form.assignment_routing"
            v-model:online-only="form.assignment_online_only"
            :agents="accountAgents"
            :inboxes="whatsappInboxes"
          />

          <!-- Enabled toggle -->
          <label class="flex items-center gap-2">
            <input v-model="form.enabled" type="checkbox" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.ENABLED') }}
            </span>
          </label>

          <div class="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              icon="i-lucide-save"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.SAVE')"
              :is-loading="isSaving"
              :disabled="!isFormValid"
              data-testid="salvar-captura"
            />
            <Button
              type="button"
              variant="outline"
              icon="i-lucide-arrow-left"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.CANCEL')"
              @click="cancelForm"
            />
          </div>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
